import React from "react";
import { Button, Col, Modal, OverlayTrigger, Popover, Row, Spinner, Tooltip } from "react-bootstrap";
import { LinkTableActionProps, LinkTableActionState, LinkTableProps, LinkTableState } from "../../types/appTypes";
import { KanbanizeBoard, RepoLink } from "../../types/kanbanizeTypes";
import { BarChartSteps, ExclamationTriangle, Pen, Trash, Clipboard } from "react-bootstrap-icons";
import { doDelete, refreshBoards, refreshRepolinksAndRepos, refreshUserRepos } from "../../utils/utils";
import { Navigate } from "react-router-dom";
import MaterialReactTable from "material-react-table";

export default class LinkTable extends React.Component<LinkTableProps, LinkTableState> {
	constructor(props: LinkTableProps) {
		super(props);
		const linkTableState: LinkTableState = {
			loadingTable: this.props.listConfig.length === 0,
			newIntegration: false,
			columns: [
				{
					accessorKey: "repo_name",
					header: "Repository",
				},
				{
					accessorFn: item => this.getBoardName(item.board_id),
					header: "Board",
				},
				{
					accessorFn: item => (
						<td className="d-flex">
							<Actions {...this.getActionProps(item)} />
						</td>
					),
					header: "Actions",
					enableColumnFilter: false,
				},
			],
			columnFilters: [],
			pagination: {
				pageIndex: 0,
				pageSize: 5,
			},
			sorting: [],
			isError: false,
			loading: this.props.listConfig.length === 0,
			refetching: false,
		};
		this.state = linkTableState;
		this.props.setCurrentComponent("LinkTable");
		this.getAuthorizedRepoConfig();
	}

	getBoardList = async () => {
		console.log("getBoardList: init");
		try {
			const boards = await refreshBoards(this.props.integrationDomain, this.props.userToken);
			this.props.setBoards(boards);
		} catch (e: any) {
			this.setState({ loadingTable: false }, () => this.props.setGeneralError(e.message));
		}
	};

	getAuthorizedRepoConfig = async () => {
		console.log("getAuthorizedRepoConfig: init");
		try {
			if (!this.props.boards || this.props.boards.length === 0) await this.getBoardList();
			const links_and_repos = await refreshRepolinksAndRepos(
				this.props.integrationDomain,
				this.props.kanbanizeDomain,
				this.props.userToken,
			);

			this.props.setListConfig(links_and_repos.savedConfigs);
			this.props.setRepositories(links_and_repos.allRepos);
			this.setState({ loadingTable: false, loading: false });
		} catch (e: any) {
			this.setState({ loadingTable: false, loading: false }, () => this.props.setGeneralError(e.message));
		}
	};

	deleteRepolink = (repoLinkId: number) => {
		this.props.setListConfig(this.props.listConfig.filter(item => item.id !== repoLinkId));
	};

	getActionProps = (item: RepoLink) => {
		const props: LinkTableActionProps = {
			integrationDomain: this.props.integrationDomain,
			userToken: this.props.userToken,
			actionConfig: item,
			setCurrentConfig: this.props.setCurrentConfig,
			setGeneralError: (message: string) => {
				this.props.setGeneralError(message);
			},
			deleteRepolink: this.deleteRepolink,
		};
		return props;
	};

	getBoardName = (boardId: number) => {
		const board: KanbanizeBoard | undefined = this.props.boards?.filter(item => item.board_id === boardId)[0];
		if (!board) {
			return boardId;
		}
		return board.name;
	};

	setNewIntegration = () => {
		this.props.setCurrentConfig({ kanbanize_domain: this.props.kanbanizeDomain } as RepoLink);
		this.setState({ newIntegration: true });
	};

	refreshUserRepos = async () => {
		try {
			this.setState({ loadingTable: true, loading: true });
			this.props.setListConfig([]);
			const result = await refreshUserRepos(this.props.integrationDomain);
			this.props.setListConfig(result.savedConfigs);
			this.props.setRepositories(result.allRepos);
			this.setState({ loadingTable: false, loading: false });
		} catch (e: any) {
			this.props.setListConfig([]);
			this.props.setGeneralError(e.message);
			this.setState({ loadingTable: false, loading: false });
		}
	};

	render(): React.ReactNode {
		console.log("reload table");
		const rowCount = this.props.listConfig.length;
		return (
			<>
				{this.state.newIntegration && <Navigate to="/linkForm" replace={true} />}
				<MaterialReactTable
					columns={this.state.columns}
					data={this.props.listConfig}
					getRowId={(row, idx) => (row.id ? row.id.toString() : idx.toString())}
					initialState={{ showColumnFilters: false }}
					onColumnFiltersChange={columnFilter => {
						if (typeof columnFilter == "function") {
							this.setState((stt, _prp) => {
								return { columnFilters: columnFilter(stt.columnFilters) };
							});
						} else {
							this.setState({ columnFilters: columnFilter });
						}
					}}
					onPaginationChange={pagination => {
						if (typeof pagination == "function") {
							this.setState((stt, _prp) => {
								return { pagination: pagination(stt.pagination) };
							});
						} else {
							this.setState({ pagination: pagination });
						}
					}}
					onSortingChange={sorting => {
						if (typeof sorting == "function") {
							this.setState((stt, _prp) => {
								return { sorting: sorting(stt.sorting) };
							});
						} else {
							this.setState({ sorting: sorting });
						}
					}}
					enableGlobalFilter={false}
					rowCount={rowCount}
					muiToolbarAlertBannerProps={
						this.state.isError
							? {
									color: "error",

									children: "Error loading data",
							  }
							: undefined
					}
					state={{
						columnFilters: this.state.columnFilters,
						// globalFilter,
						isLoading: this.props.refreshing,
						pagination: this.state.pagination,
						showAlertBanner: this.state.isError,
						showProgressBars: this.state.refetching,
						sorting: this.state.sorting,
					}}
				></MaterialReactTable>

				{this.state.loadingTable && (
					<Row>
						<Col></Col>
						<Col className="text-center">
							<p>Loading your repository list...</p>
							<Spinner className="m-3" style={{ width: "3rem", height: "3rem" }} animation="border" variant="primary" />
							<p>
								Please, be patient. Dependining on the number of repositories you have access to, the process can take up
								to 1 minute
							</p>
						</Col>
						<Col></Col>
					</Row>
				)}
				{(process.env.REACT_APP_INTEGRATION_SERVICE !== "BitBucket" && (
					<Row className="m-3 text-start">
						<p>
							Note: If you want to refresh the repositories, please, click{" "}
							<a
								href="/#"
								onClick={e => {
									e.preventDefault();
									this.refreshUserRepos();
								}}
							>
								here
							</a>
						</p>
					</Row>
				)) || (
					<Row>
						<p></p>
					</Row>
				)}
				<Row>
					<Col></Col>
					<Col>
						<Button className="col-12" size="lg" variant="primary" onClick={this.setNewIntegration}>
							Create new link
						</Button>
					</Col>

					<Col></Col>
				</Row>
			</>
		);
	}
}

class Actions extends React.Component<LinkTableActionProps, LinkTableActionState> {
	constructor(props: LinkTableActionProps) {
		super(props);
		const actionState: LinkTableActionState = {
			isEdit: false,
			isDelete: false,
			deleted: false,
			repoName: undefined,
			appInstallWarning: true,
			errorHistory: false,
		};
		this.state = actionState;
	}

	setEdit = () => {
		this.props.setCurrentConfig(this.props.actionConfig);
		this.setState({ isEdit: true });
	};

	repoNameChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const repoName = event.target.value;
		this.setState({ repoName });
	};

	handleError = (message: string) => {
		this.props.setGeneralError(message);
	};

	setDelete = (state: boolean) => {
		this.setState({ isDelete: state });
	};

	deleteConfig = async () => {
		try {
			if (process.env.REACT_APP_INTEGRATION_SERVICE !== "GitHub") {
				await doDelete(`${this.props.integrationDomain}/api/db/repoLink/${this.props.actionConfig.id}`);
			} else {
				if (!this.props.actionConfig.id) throw new Error("id not found for the repository link");
				await doDelete(`${this.props.integrationDomain}/api/config/${this.props.actionConfig.id}`, this.props.userToken);
			}
			this.setState({ deleted: true, isDelete: false });
		} catch (e: any) {
			this.props.setGeneralError(e.message);
			this.setState({ isDelete: false });
		}
	};

	hideDeletedModal = () => {
		this.setState({ deleted: false });
		if (!this.props.actionConfig.id) throw new Error("id not found for the repository link");
		this.props.deleteRepolink(this.props.actionConfig.id);
	};
	setErrorHistory = () => {
		this.props.setCurrentConfig(this.props.actionConfig);
		this.setState({ errorHistory: true });
	};

	cloneItem = () => {
		const cloned_config = this.props.actionConfig;
		cloned_config.id = undefined;
		this.props.setCurrentConfig(cloned_config);
		this.setState({ isEdit: true });
	};

	render(): React.ReactNode {
		return (
			<>
				{this.state.isEdit && <Navigate to="/linkForm" replace={true} />}
				{this.state.errorHistory && <Navigate to="/errHistory" replace={true} />}
				<OverlayTrigger
					placement="top"
					delay={{ show: 250, hide: 400 }}
					overlay={<Tooltip id="button-tooltip-edit">Edit</Tooltip>}
				>
					<Button className="ms-3 d-flex align-items-center py-2" variant="outline-primary" onClick={this.setEdit}>
						<Pen />
					</Button>
				</OverlayTrigger>
				<OverlayTrigger
					placement="top"
					delay={{ show: 250, hide: 400 }}
					overlay={<Tooltip id="button-tooltip-edit">Delete</Tooltip>}
				>
					<Button
						className="ms-3 d-flex align-items-center py-2"
						variant="outline-danger"
						onClick={() => {
							this.setDelete(true);
						}}
					>
						<Trash />
					</Button>
				</OverlayTrigger>
				<OverlayTrigger
					placement="top"
					delay={{ show: 250, hide: 400 }}
					overlay={<Tooltip id="button-tooltip-edit">Clone</Tooltip>}
				>
					<Button className="ms-3 d-flex align-items-center py-2" variant="outline-primary" onClick={this.cloneItem}>
						<Clipboard />
					</Button>
				</OverlayTrigger>
				{!!this.props.actionConfig.errors_number && this.props.actionConfig.errors_number > 0 && (
					<OverlayTrigger
						placement="top"
						delay={{ show: 250, hide: 400 }}
						overlay={<Tooltip id="button-tooltip-edit">Error log</Tooltip>}
					>
						<Button className="ms-3 d-flex align-items-center py-2" variant="outline-danger" onClick={this.setErrorHistory}>
							<BarChartSteps />
						</Button>
					</OverlayTrigger>
				)}
				{this.props.actionConfig.warnings.length > 0 && (
					<OverlayTrigger
						trigger="hover"
						placement="bottom"
						overlay={
							<Popover id={`popover-for-${this.props.actionConfig.repo_name}`}>
								<Popover.Header as="h3">{`Warnings in ${this.props.actionConfig.repo_name} repolink`}</Popover.Header>

								{this.props.actionConfig.warnings.map(item => (
									<Popover.Body>
										<strong>·</strong> {item}
									</Popover.Body>
								))}
							</Popover>
						}
					>
						<Button className="ms-3 d-flex align-items-center py-2" variant="outline-warning">
							<ExclamationTriangle />
						</Button>
					</OverlayTrigger>
				)}
				<Modal
					show={this.state.isDelete}
					onHide={() => {
						this.setDelete(false);
					}}
				>
					<Modal.Header closeButton>
						<Modal.Title>Warning</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						Are you sure that you want to remove the configuration for {this.props.actionConfig.repo_name}?
						{process.env.REACT_APP_INTEGRATION_SERVICE === "GitLab" && (
							<p>The Webhooks pointing to this integration will also be deleted.</p>
						)}
						{process.env.REACT_APP_INTEGRATION_SERVICE === "GitHub" && (
							<p>
								To invalidate Webhooks and Permissions granted through the app please uninstall the app from the GitHub
								repository.
							</p>
						)}
						{process.env.REACT_APP_INTEGRATION_SERVICE === "BitBucket" && (
							<p>
								To invalidate Webhooks and Permissions granted through the app please uninstall the app from the BitBucket
								workspace. Take into account that doing this will make the integration lose access to ALL repositories
								inside the integration.
							</p>
						)}
					</Modal.Body>
					<Modal.Footer>
						<Button
							variant="secondary"
							onClick={() => {
								this.setDelete(false);
							}}
						>
							Close
						</Button>
						<Button variant="danger" onClick={this.deleteConfig}>
							Delete
						</Button>
					</Modal.Footer>
				</Modal>
				<Modal show={this.state.deleted} onHide={this.hideDeletedModal}>
					<Modal.Header closeButton>
						<Modal.Title>Info</Modal.Title>
					</Modal.Header>
					<Modal.Body>The configuration for {this.props.actionConfig.repo_name} has been deleted</Modal.Body>
					<Modal.Footer>
						<Button variant="secondary" onClick={this.hideDeletedModal}>
							Close
						</Button>
					</Modal.Footer>
				</Modal>
			</>
		);
	}
}
